import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCoverageCategories,
  getCoverageTrainings,
  getTrainingCoverageDetail,
} from "./services/trainingCoverageService";
import { PAGE_SIZE } from "./helpers";

/**
 * Owns the Training Coverage drill-down's data and navigation state (categories →
 * trainings → detail). Search is debounced and every fetch is stamped with a request
 * id, so a slow response from an earlier click or financial year can never overwrite a
 * newer view. Each load exposes an error flag and a retry so a failed fetch shows a
 * recoverable message instead of a silent empty page or a stuck skeleton.
 *
 * @param {number} financialYearId - selected financial year from the dashboard filter
 * @returns {object} the current view state plus the handlers the view calls
 */
export function useTrainingCoverage(financialYearId) {
  const [view, setView] = useState("categories");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [trainings, setTrainings] = useState({ items: [], total: 0 });
  const [trainingsError, setTrainingsError] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(false);

  const reqId = useRef(0);
  const searchTimer = useRef();
  const lastTrainingIds = useRef([]);

  const loadCategories = useCallback(async () => {
    if (!financialYearId) return;
    const id = ++reqId.current;
    setCategoriesError(false);
    setLoading(true);
    const data = await getCoverageCategories(financialYearId);
    if (id !== reqId.current) return;
    if (data === null) {
      setCategoriesError(true);
      setLoading(false);
      return;
    }
    setCategories(data);
    setLoading(false);
  }, [financialYearId]);

  const loadTrainings = useCallback(
    async (categoryId, nextPage, searchTerm) => {
      const id = ++reqId.current;
      setTrainingsError(false);
      setLoading(true);
      const data = await getCoverageTrainings(
        categoryId,
        financialYearId,
        PAGE_SIZE,
        nextPage * PAGE_SIZE,
        searchTerm
      );
      if (id !== reqId.current) return;
      if (data === null) {
        setTrainingsError(true);
        setLoading(false);
        return;
      }
      setTrainings(data);
      setLoading(false);
    },
    [financialYearId]
  );

  const openTraining = useCallback(async (trainingIds) => {
    lastTrainingIds.current = trainingIds;
    const id = ++reqId.current;
    setDetailError(false);
    setDetail(null);
    setLoading(true);
    setView("detail");
    const data = await getTrainingCoverageDetail(trainingIds);
    if (id !== reqId.current) return;
    if (!data) {
      setDetailError(true);
      setLoading(false);
      return;
    }
    setDetail(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    setView("categories");
    setActiveCategory(null);
    loadCategories();
  }, [loadCategories]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const openCategory = useCallback(
    (category) => {
      setActiveCategory(category);
      setPage(0);
      setSearch("");
      setView("trainings");
      loadTrainings(category.id, 0, "");
    },
    [loadTrainings]
  );

  const onSearchChange = useCallback(
    (value) => {
      setSearch(value);
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        setPage(0);
        if (activeCategory) loadTrainings(activeCategory.id, 0, value);
      }, 300);
    },
    [activeCategory, loadTrainings]
  );

  const changePage = useCallback(
    (nextPage) => {
      setPage(nextPage);
      if (activeCategory) loadTrainings(activeCategory.id, nextPage, search);
    },
    [activeCategory, search, loadTrainings]
  );

  const goToCategories = useCallback(() => setView("categories"), []);

  const retryTrainings = useCallback(() => {
    if (activeCategory) loadTrainings(activeCategory.id, page, search);
  }, [activeCategory, page, search, loadTrainings]);

  const retryDetail = useCallback(() => {
    openTraining(lastTrainingIds.current);
  }, [openTraining]);

  return {
    view,
    loading,
    categories,
    categoriesError,
    activeCategory,
    trainings,
    trainingsError,
    page,
    search,
    detail,
    detailError,
    openCategory,
    onSearchChange,
    changePage,
    openTraining,
    goToCategories,
    retryCategories: loadCategories,
    retryTrainings,
    retryDetail,
  };
}
